import { useState, type FormEvent } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { Building2, Eye, EyeOff, KeyRound, User as UserIcon } from 'lucide-react'
import { toast } from 'sonner'
import { changePassword, fetchEmpresa } from '../api'
import { useAuth } from '../AuthContext'
import { RANK_LABELS } from '../lib/rank'
import { PageHeader } from '../components/PageHeader'
import { UserAvatar } from '../components/UserAvatar'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Badge } from '../components/ui/badge'
import { Skeleton } from '../components/ui/skeleton'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'

const emptyForm = { currentPassword: '', newPassword: '' }

export function PerfilPage() {
  const { user, activeSociedadId, logout } = useAuth()
  const [form, setForm] = useState(emptyForm)
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const activeMembership = user?.sociedades.find((s) => s.id === activeSociedadId)
  const roleLabel = user?.is_admin && !activeMembership ? 'SuperAdmin de plataforma' : activeMembership ? RANK_LABELS[activeMembership.rank] : null
  const isOwner = activeMembership?.rank === 'owner'

  const empresaQuery = useQuery({
    queryKey: ['empresa', activeMembership?.empresa_id],
    queryFn: () => fetchEmpresa(activeMembership!.empresa_id),
    enabled: isOwner,
  })

  const mutation = useMutation({
    mutationFn: () => changePassword(form.currentPassword, form.newPassword),
    onSuccess: async () => {
      toast.success('Contraseña actualizada — iniciá sesión de nuevo con la nueva.')
      await logout()
    },
    onError: (err) => {
      if (axios.isAxiosError(err) && err.response?.status === 400) {
        setError('La contraseña actual no es correcta.')
      } else {
        setError('No se pudo cambiar la contraseña. Intentá de nuevo.')
      }
    },
  })

  function handleSubmit(e: FormEvent): void {
    e.preventDefault()
    setError(null)
    mutation.mutate()
  }

  if (!user) return null

  return (
    <div className="flex flex-col gap-6">
      <PageHeader icon={UserIcon} title="Perfil" description="Tu información de cuenta." />

      <Card>
        <CardContent className="flex flex-wrap items-center gap-4 pt-6">
          <UserAvatar name={user.nombre} seed={user.email} size="md" />
          <div className="flex-1">
            <p className="text-base font-semibold">{user.nombre}</p>
            <p className="text-sm text-muted-foreground">{user.email}</p>
          </div>
          <div className="flex flex-col items-end gap-1 text-right">
            {roleLabel && <Badge variant="outline">{roleLabel}</Badge>}
            <p className="text-xs text-muted-foreground">
              Miembro desde {new Date(user.created_at).toLocaleDateString('es-CR')}
            </p>
          </div>
        </CardContent>
      </Card>

      {isOwner && (
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2 className="size-4" />
              Tu Empresa
            </CardTitle>
          </CardHeader>
          <CardContent>
            {empresaQuery.isLoading && (
              <div className="flex flex-col gap-2.5">
                <Skeleton className="h-5 w-2/3" />
                <Skeleton className="h-5 w-1/2" />
                <Skeleton className="h-5 w-1/3" />
              </div>
            )}
            {empresaQuery.isError && (
              <p className="text-sm text-destructive">No se pudo cargar la información de la Empresa.</p>
            )}
            {empresaQuery.data && (
              <dl className="flex flex-col gap-2 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-muted-foreground">Nombre</dt>
                  <dd className="font-medium">{empresaQuery.data.nombre}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-muted-foreground">Email de contacto</dt>
                  <dd className="font-medium">{empresaQuery.data.email_contacto ?? '—'}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-muted-foreground">Sociedades</dt>
                  <dd className="font-medium">{empresaQuery.data.sociedades.length}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-muted-foreground">Alta</dt>
                  <dd className="font-medium">
                    {new Date(empresaQuery.data.created_at).toLocaleDateString('es-CR')}
                  </dd>
                </div>
              </dl>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <KeyRound className="size-4" />
            Cambiar contraseña
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="perfil-current-password">Contraseña actual</Label>
              <div className="relative">
                <Input
                  id="perfil-current-password"
                  type={showCurrent ? 'text' : 'password'}
                  required
                  value={form.currentPassword}
                  onChange={(e) => setForm({ ...form, currentPassword: e.target.value })}
                  className="pr-9"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent((v) => !v)}
                  aria-label={showCurrent ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                >
                  {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="perfil-new-password">Contraseña nueva</Label>
              <div className="relative">
                <Input
                  id="perfil-new-password"
                  type={showNew ? 'text' : 'password'}
                  required
                  minLength={8}
                  value={form.newPassword}
                  onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
                  className="pr-9"
                />
                <button
                  type="button"
                  onClick={() => setShowNew((v) => !v)}
                  aria-label={showNew ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                >
                  {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && <p className="rounded-lg bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive">{error}</p>}

            <Button type="submit" disabled={mutation.isPending} className="self-start">
              Guardar contraseña
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
